import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowRight, ChevronDown, Search, X } from "lucide-react";
import { faqSections } from "./faqData";
import { openDemoModal } from "../utils/demoModal";
import "./FaqPage.css";

const topics = [
  ["Platform", 2], ["AI Interviews", 3], ["Assessments", 4],
  ["Proctoring", 5], ["Evaluation", 6], ["Use Cases", 9], ["Security", 12],
];
const categoryLabels = [
  "About Viriqo Hires", "Platform & Workflow", "AI-Powered Interviews",
  "Technical & Coding Assessments", "Interview Monitoring & Proctoring",
  "Candidate Evaluation & Scoring", "Reports, Recordings & Review",
  "Interview Management", "Use Cases", "Teams & User Management",
  "Candidate Experience", "Enterprise, Security & Integrations", "Getting Started",
];

function Highlight({ text, query }) {
  if (!query.trim()) return text;
  const words = query.trim().split(/\s+/).filter(Boolean).map((word) => word.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  if (!words.length) return text;
  return String(text).split(new RegExp(`(${words.join("|")})`, "ig")).map((part, index) =>
    words.some((word) => part.toLowerCase() === word.toLowerCase()) ? <mark key={`${part}-${index}`}>{part}</mark> : part
  );
}

function useVisibleSections(visibleSections) {
  const [active, setActive] = useState(1);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const nodes = visibleSections.map(({ id }) => document.getElementById(`s${String(id).padStart(2, "0")}`)).filter(Boolean);
    const observer = new IntersectionObserver((entries) => {
      const visible = entries.filter((entry) => entry.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
      if (visible[0]) setActive(Number(visible[0].target.dataset.section));
    }, { rootMargin: "-18% 0px -68% 0px", threshold: 0 });
    nodes.forEach((node) => observer.observe(node));
    const updateProgress = () => {
      const body = document.getElementById("faqBody");
      if (!body) return;
      const rect = body.getBoundingClientRect();
      const scanline = window.innerHeight * 0.35;
      setProgress(Math.min(100, Math.max(0, ((scanline - rect.top) / rect.height) * 100)));
    };
    updateProgress();
    window.addEventListener("scroll", updateProgress, { passive: true });
    window.addEventListener("resize", updateProgress);
    return () => { observer.disconnect(); window.removeEventListener("scroll", updateProgress); window.removeEventListener("resize", updateProgress); };
  }, [visibleSections]);
  return { active, progress };
}

export default function FaqPage() {
  const [query, setQuery] = useState("");
  const [openIds, setOpenIds] = useState(() => new Set(["q-1-1"]));
  const location = useLocation();
  const navigate = useNavigate();
  const searchRef = useRef(null);
  const expandedAll = useMemo(() => {
    const words = query.toLowerCase().trim().split(/\s+/).filter(Boolean);
    const visible = faqSections.flatMap((section) => section.questions).filter((item) =>
      !words.length || words.every((word) => `${item.question} ${item.answer}`.toLowerCase().includes(word))
    );
    return visible.length > 0 && visible.every((item) => openIds.has(`q-${item.id}`));
  }, [openIds, query]);

  const filteredSections = useMemo(() => faqSections.map((section) => ({
    ...section,
    questions: section.questions.filter((item) => {
      if (!query.trim()) return true;
      const words = query.toLowerCase().trim().split(/\s+/);
      const text = `${item.question} ${item.answer}`.toLowerCase();
      return words.every((word) => text.includes(word));
    }),
  })).filter((section) => section.questions.length), [query]);
  const resultCount = filteredSections.reduce((count, section) => count + section.questions.length, 0);
  const { active, progress } = useVisibleSections(filteredSections);

  useEffect(() => {
    if (!query.trim()) return;
    const firstWord = query.toLowerCase().trim().split(/\s+/)[0];
    const visibleQuestions = filteredSections.flatMap((section) => section.questions);
    setOpenIds((previous) => {
      const next = new Set(previous);
      visibleQuestions.forEach((item) => {
        const id = `q-${item.id}`;
        if (item.answer.toLowerCase().includes(firstWord)) next.add(id);
        else next.delete(id);
      });
      return next;
    });
  }, [query, filteredSections]);

  useEffect(() => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const nodes = document.querySelectorAll(".tt-faq .tt-faq-cta.tt-faq-reveal");
    if (reduced || query) {
      nodes.forEach((node) => node.classList.add("is-visible"));
      return undefined;
    }
    const observer = new IntersectionObserver((entries) => entries.forEach((entry) => {
      if (entry.isIntersecting) { entry.target.classList.add("is-visible"); observer.unobserve(entry.target); }
    }), { threshold: 0.08 });
    nodes.forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, [filteredSections, query]);

  useEffect(() => {
    if (!location.hash) return;
    const targetId = decodeURIComponent(location.hash.slice(1));
    const questionMatch = targetId.match(/^q-(\d+)-(\d+)$/);
    if (questionMatch) {
      const id = `q-${questionMatch[1]}-${questionMatch[2]}`;
      setOpenIds((previous) => new Set(previous).add(id));
      window.setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "center" }), 100);
    }
  }, [location.hash]);

  const toggleQuestion = useCallback((id) => setOpenIds((previous) => {
    const next = new Set(previous);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  }), []);

  const goToSection = (event, sectionId) => {
    event.preventDefault();
    const anchor = `#s${String(sectionId).padStart(2, "0")}`;
    if (query && !filteredSections.some((section) => section.id === sectionId)) {
      setQuery("");
      window.setTimeout(() => document.getElementById(anchor.slice(1))?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
      navigate(`${location.pathname}${anchor}`);
      return;
    }
    if (location.hash !== anchor) navigate(`${location.pathname}${anchor}`, { replace: false });
    document.getElementById(anchor.slice(1))?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const requestDemo = () => openDemoModal();

  return (
    <main className="tt-faq" id="top">
      <section className="tt-faq-hero">
        <span className="tt-faq-bubble bubble-one" aria-hidden="true">?</span>
        <span className="tt-faq-bubble bubble-two" aria-hidden="true">?</span>
        <span className="tt-faq-bubble bubble-three" aria-hidden="true">?</span>
        <span className="tt-faq-bubble bubble-four" aria-hidden="true">!</span>
        <div className="tt-faq-wrap tt-faq-hero-content">
          <span className="tt-faq-eyebrow">FAQ</span>
          <h1>Frequently asked questions about <span>Viriqo Hires</span></h1>
          <p className="tt-faq-lead">Get answers about Viriqo Hires' AI-powered technical interviews, coding assessments, interview monitoring, candidate evaluation, and more.</p>
          <div className={`tt-faq-search${query ? " has-query" : ""}`}>
            <Search size={22} aria-hidden="true" />
            <label className="tt-faq-sr-only" htmlFor="faq-search">Search the FAQ</label>
            <input ref={searchRef} id="faq-search" type="search" value={query} onChange={(event) => setQuery(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); document.getElementById("faqBody")?.scrollIntoView({ behavior: "smooth" }); } }} placeholder="Search questions, e.g. second camera" autoComplete="off" />
            {query && <button type="button" className="tt-faq-clear" onClick={() => { setQuery(""); searchRef.current?.focus(); }} aria-label="Clear search"><span>Clear</span><X size={15} /></button>}
          </div>
          <div className="tt-faq-search-meta" aria-live="polite">{query ? `${resultCount} question${resultCount === 1 ? "" : "s"} found` : ""}</div>
          <div className="tt-faq-topics"><b>Popular topics:</b>{topics.map(([label, id]) => <a key={id} href={`#s${String(id).padStart(2, "0")}`} onClick={(event) => goToSection(event, id)}>{label}</a>)}</div>
        </div>
      </section>

      <section className="tt-faq-content" aria-label="Frequently asked questions">
        <div className="tt-faq-wrap">
          <nav className="tt-faq-mobile-cats" aria-label="FAQ categories">
            <div>{faqSections.map((section) => <a key={section.id} className={active === section.id ? "active" : ""} href={`#s${String(section.id).padStart(2, "0")}`} onClick={(event) => goToSection(event, section.id)}>{categoryLabels[section.id - 1]}</a>)}</div>
          </nav>
          <div className="tt-faq-grid">
            <aside className="tt-faq-sidebar">
              <h2>FAQ Categories</h2>
              <div className="tt-faq-progress" aria-label={`Reading progress ${Math.round(progress)} percent`}><span style={{ width: `${progress}%` }} /></div>
              <ul className="tt-faq-categories">
                {faqSections.map((section) => <li key={section.id} className={query && !filteredSections.some((visible) => visible.id === section.id) ? "dim" : ""}><a className={active === section.id ? "active" : ""} href={`#s${String(section.id).padStart(2, "0")}`} onClick={(event) => goToSection(event, section.id)}><small>{String(section.id).padStart(2, "0")}</small>{categoryLabels[section.id - 1]}</a></li>)}
              </ul>
              <div className="tt-faq-help"><h3>Still have questions?</h3><p>Our team is here to help you discover how Viriqo Hires can work for your hiring team.</p><button type="button" onClick={requestDemo}>Book a Demo <ArrowRight size={16} /></button></div>
            </aside>

            <div className="tt-faq-body" id="faqBody">
              <div className="tt-faq-tools"><span>{query ? `${resultCount} matching questions` : ""}</span><button type="button" onClick={() => {
                const visibleIds = filteredSections.flatMap((section) => section.questions.map((item) => `q-${item.id}`));
                setOpenIds((previous) => expandedAll ? new Set([...previous].filter((id) => !visibleIds.includes(id))) : new Set([...previous, ...visibleIds]));
              }}>{expandedAll ? "Collapse all" : "Expand all"}<ChevronDown className={expandedAll ? "rotated" : ""} size={16} /></button></div>
              {filteredSections.length ? filteredSections.map((section, index) => <section className={`tt-faq-section${index === 0 ? " first" : ""}${active === section.id ? " is-active" : ""}`} id={`s${String(section.id).padStart(2, "0")}`} data-section={section.id} key={section.id}>
                <div className="tt-faq-section-top"><span className="tt-faq-number">{String(section.id).padStart(2, "0")}</span><div><h2><Highlight text={section.title} query={query} /></h2><p><Highlight text={section.description} query={query} /></p></div></div>
                {section.questions.map((item) => {
                  const qid = `q-${item.id}`;
                  const answerId = `a-${item.id}`;
                  const isOpen = openIds.has(qid);
                  return <article className={`tt-faq-item${isOpen ? " open" : ""}`} id={qid} key={qid}>
                    <h3><button type="button" aria-expanded={isOpen} aria-controls={answerId} onClick={() => toggleQuestion(qid)}><span><Highlight text={item.question} query={query} /></span><i aria-hidden="true" /></button></h3>
                    <div className="tt-faq-answer" id={answerId} role="region" aria-label={item.question} aria-hidden={!isOpen}><div><p><Highlight text={item.answer} query={query} /></p></div></div>
                  </article>;
                })}
                {section.id === 1 && !query && <div className="tt-faq-callout"><span className="icon"><ArrowRight size={22} /></span><div><b>Explore more questions in the sections below</b><span>Continue scrolling to find answers about evaluation, reporting, use cases, security, and more.</span></div></div>}
              </section>) : <div className="tt-faq-empty"><span className="icon"><Search size={25} /></span><h2>No matching questions</h2><p>Try a different keyword, or talk to our team directly.</p><button type="button" onClick={() => { setQuery(""); searchRef.current?.focus(); }}>Clear search</button></div>}
            </div>
          </div>
        </div>
      </section>

      <section className="tt-faq-final">
        <div className="tt-faq-wrap"><div className="tt-faq-cta tt-faq-reveal">
          <span className="ring ring-one" aria-hidden="true" /><span className="ring ring-two" aria-hidden="true" />
          <div className="content"><span className="tt-faq-eyebrow">Still Have Questions?</span><h2>Couldn't find what you were looking for?</h2><p className="tt-faq-lead">Talk to the Viriqo Hires team about your technical hiring process, interview requirements, assessment needs, or enterprise questions.</p>
            <div className="tt-faq-ctas"><button type="button" className="primary" onClick={requestDemo}>Book a Demo <ArrowRight size={18} /></button></div>
          </div>
        </div></div>
      </section>
    </main>
  );
}
